import { useCallback, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { Notation } from "@/components/Notation";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import {
  TWINKLE_MELODY,
  flatten,
  melodyToMeasures,
  midiToPitchName,
  tonicMidiForPc,
} from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 5 — Twinkle Twinkle, Melodic Chord Inversions.
 *
 * The right hand stops being a single melodic line and becomes a stack of
 * chords — but with the melody note ALWAYS on top. The trick is to choose
 * the inversion of each chord that puts the melody note as the highest
 * voice. Adds a brief "denser harmonisation" coda showing how to extend
 * beyond I-IV-V into ii, iii, vi, suspensions and slash chords.
 */

export default function PianoIntermediate5() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "5");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="Twinkle Twinkle — Melodic Chord Inversions"
          exerciseGoal="Each melody note becomes the top of a chord. The right hand turns into a four-voice choir that follows your tune."
          bpm={68}
          difficulty="Advanced"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);

  const [activePcs, setActivePcs] = useState<Set<number>>(new Set());
  const onMidiNote = useCallback((p: number) => {
    setActivePcs((prev) => {
      const next = new Set(prev);
      next.add(p);
      setTimeout(() => {
        setActivePcs((cur) => {
          const n = new Set(cur);
          n.delete(p);
          return n;
        });
      }, 300);
      return next;
    });
  }, []);

  // Master keyboard: I in root position (showing the chord shape we'll
  // re-arrange so the melody sits on top).
  const litHighlights: KeyHighlight[] = [
    { degree: 1, tone: "root",  label: "1" },
    { degree: 3, tone: "voice", label: "3" },
    { degree: 5, tone: "voice", label: "5" },
  ];

  // Demo: play the I chord in its three positions (root / 1st / 2nd) so the
  // student can hear how the same chord sits underneath different melody
  // notes.
  const playThreeInversions = async () => {
    kickAudio();
    const root = tonicMidi;          // root position: 1 - 3 - 5
    const i1 = tonicMidi + 4;        // 1st inversion: 3 - 5 - 1(oct)
    const i2 = tonicMidi + 7;        // 2nd inversion: 5 - 1(oct) - 3(oct)
    const buildChord = (bottom: number, top: number) => {
      // Stack root-3rd-5th of the I chord, but starting from `bottom`,
      // and pushing voices up an octave until the top voice == `top`.
      const tones = [0, 4, 7];
      const out: number[] = [];
      let m = bottom;
      let i = 0;
      while (m <= top + 12) {
        const pc = tonicMidi + tones[i % 3];
        // place pc above the previous voice
        let placed = pc;
        while (placed < m) placed += 12;
        out.push(placed);
        m = placed + 1;
        i += 1;
        if (out.length >= 3) break;
      }
      return out.map((mm) => midiToPitchName(mm, k.useFlats));
    };
    // One chord per half note at the shell's 68 BPM (1.76 s, not 1.6 s).
    const BPM = 68;
    const half = (60 / BPM) * 2;
    await playSequence(
      [
        { time: 0,        notes: buildChord(root, root + 7), duration: "2n" },
        { time: half,     notes: buildChord(i1,   i1 + 7),   duration: "2n" },
        { time: half * 2, notes: buildChord(i2,   i2 + 7),   duration: "2n" },
      ],
      BPM
    );
  };

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={litHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="Tonic chord shape — root, third, fifth"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          The chord stays the same. The <em>arrangement</em> on the keyboard
          changes so the highest voice matches the melody note.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="The melody note rides on top of every chord">
          <p>
            Until now the melody has been a single line. In this lesson the
            right hand thickens it: <strong>every melody note becomes the
            top voice of a three- or four-note chord</strong>. The chord
            doesn't change identity — it's still I or IV or V — but the
            <em> inversion</em> we choose puts the melody note as the
            highest sounding pitch.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Let's say you're doing C major and if you want C major for
            Twinkle, you kind of know that C is at the top end and needs to be
            at the top end and the rest of the notes of the chord need to be
            under it in order to serve that melody."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Three inversions of one chord" title="Pick the one whose top note is the melody">
          <p>
            Every triad has three positions. The chord is the same — only
            the order of voices changes. Top voice = the note your ear
            hears as the melody.
          </p>
          <InversionStack tonicPc={k.tonicPc} useFlats={k.useFlats} />
          <p>
            So if your melody note is the chord's root, you want 1st
            inversion. If it's the 3rd, you want 2nd inversion. If it's the
            5th, root position. Click below to hear the tonic chord in all
            three positions.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={playThreeInversions}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Hear all three positions of I in {k.tonicName}
            </button>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/04-twinkle-twinkle-left-hand-arpeggios-page1.png"
          caption="Jason's chart — the inversion under every melody note, with the alternative chords he offers."
        />
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Twinkle in chord-melody style" title="The melody on the staff (transposes with the key)">
          <p>
            Below is the melody in the current key. In this lesson, every
            note you see is the <em>top voice</em> of a chord stacked
            underneath. The left hand keeps the simple bass from Lesson 3.
            The right hand thickens.
          </p>
          <Notation
            measures={melodyToMeasures(TWINKLE_MELODY, tonicMidi, 4, k.useFlats)}
            timeSig="4/4"
            width={1280}
            height={170}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Pedal trick" title="Use the sustain pedal to smooth the changes">
          <p>
            Stacking three or four voices on every melody note can feel
            choppy. Two tools fix that:
          </p>
          <ol className="my-3 space-y-2 text-[14px] text-cyan-50/95">
            <li>
              <strong>1. The sustain pedal.</strong> Hold one chord's pedal,
              lift it the moment you hit the next chord, hold again. The
              chords blur into each other slightly — exactly what gives the
              effect a <em>choral</em> feel rather than a stabby keyboard
              feel.
            </li>
            <li>
              <strong>2. Hold the harmony fingers.</strong> Slam the chord,
              then keep the fingers that are not playing the melody held
              down while the melody finger moves on. It's a little
              finger-independence exercise inside the main one.
            </li>
          </ol>
          <PIInsight>
            <p>
              This is the same principle a vocal arranger uses. A choir
              moves between chords by keeping the singer with the held note
              still, and only switching the voices that need to move.
              Listen to a great choir sing "Amen" — that's voice-leading.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>


      <FadeIn>
        <Drill number="5" label="Beyond I-IV-V" title="Denser harmonisations">
          <p>
            Once the basic chord-melody is clean, swap a few chords for
            neighbours from the same key that still contain the melody note —
            the alternatives on Jason's chart:
          </p>
          <ul className="my-3 space-y-1.5 text-[14px] text-cyan-50/95">
            <li><strong>iii</strong> ({k.nameForPc(k.pcFromDegree(3))} minor) or <strong>{k.nameForPc(k.pcFromDegree(1))}/{k.nameForPc(k.pcFromDegree(3))}</strong> (the tonic with its 3rd in the bass) under the melody's 5th.</li>
            <li><strong>vi</strong> ({k.nameForPc(k.pcFromDegree(6))} minor) in place of the tonic under the melody's 3rd.</li>
            <li><strong>ii</strong> ({k.nameForPc(k.pcFromDegree(2))} minor) or <strong>V7</strong> under the melody's 4th.</li>
            <li><strong>{k.nameForPc(k.pcFromDegree(5))}sus4 → {k.nameForPc(k.pcFromDegree(5))}</strong> under the melody's 2nd — the suspended 4th resolves down to the 3rd.</li>
          </ul>
          <PIQuote attribution="Jason · lesson video">
            "F major, G7, C major, A minor, D over F bass, G, C, sus, yeah.
            Let me just give it a go so you get the feel of it."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="Choral Twinkle in the chosen key">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="5"
            label={`Choral Twinkle · ${k.tonicName}`}
            originalBpm={68}
            onNote={onMidiNote}
          />
          {activePcs.size > 0 && (
            <PianoDiagram
              octaves={2}
              startOctave={3}
              compact
              highlights={[...activePcs].map((p): KeyHighlight => ({ pc: p, tone: "voice" }))}
            />
          )}
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={<>The first three rungs build the inversion-on-top reflex. The fourth opens the door to the denser harmonisation.</>}
            rungs={[
              { title: "Identify the inversion",     minutes: 4, accent: "amber",   detail: "For each melody note in line 1, work out: which inversion of which chord puts THIS note on top? Speak it out loud — root / 1st / 2nd. No playing yet." },
              { title: "Play it slow, no pedal",      minutes: 4, accent: "cyan",    detail: "Quarter note = beat. RH chord-melody, LH single root. No pedal yet — let yourself hear every gap." },
              { title: "Add the sustain pedal",       minutes: 4, accent: "rose",    detail: "Pedal down on every chord change, lift on the next attack. Smooth the seams." },
              { title: "Denser harmonisation",        minutes: 4, accent: "emerald", detail: "Try the alternatives from Jason's chart — iii for the tonic over its 3rd, vi for the tonic, ii for V7, sus4 resolving to V. Stay inside the key — the melody decides." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* —— Bespoke visual: three positions side-by-side, with voice-leading
       arrows showing where the top voice (the melody) goes. —— */
function InversionStack({ tonicPc, useFlats }: { tonicPc: number; useFlats: boolean }) {
  // Voicings expressed as semitone offsets from the tonic, in stack order
  // (bottom → top). Major triad only (this lesson is in major).
  const inversions = [
    { name: "Root position",  voicing: [0, 4, 7],   topRole: "5th",  topDegreeIdx: 5 },
    { name: "1st inversion",  voicing: [4, 7, 12],  topRole: "root", topDegreeIdx: 1 },
    { name: "2nd inversion",  voicing: [7, 12, 16], topRole: "3rd",  topDegreeIdx: 3 },
  ];
  const SHARP = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
  const FLAT  = ["C","Db","D","Eb","E","F","Gb","G","Ab","A","Bb","B"];
  const nameOf = (semis: number) => {
    const pc = ((tonicPc + semis) % 12 + 12) % 12;
    return (useFlats ? FLAT : SHARP)[pc];
  };

  return (
    <div className="my-4 grid grid-cols-1 gap-3 md:grid-cols-3">
      {inversions.map((inv) => (
        <motion.div
          key={inv.name}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="rounded-2xl border border-cyan-400/25 bg-zinc-900/40 p-3"
        >
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-amber-300">
              {inv.name}
            </div>
            <div className="rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-mono text-amber-200">
              top = {inv.topRole}
            </div>
          </div>

          {/* mini stack: three rectangles vertically, top-most highlighted */}
          <div className="mt-2 flex flex-col-reverse gap-1.5">
            {inv.voicing.map((semis, i) => {
              const isTop = i === inv.voicing.length - 1;
              return (
                <motion.div
                  key={i}
                  layout
                  className={`flex items-center justify-between rounded-md border px-2.5 py-1.5 text-[12px] font-semibold ${
                    isTop
                      ? "border-amber-300 bg-amber-400/20 text-amber-100"
                      : "border-cyan-400/25 bg-cyan-500/10 text-cyan-200"
                  }`}
                >
                  <span className="font-mono text-[11px] uppercase tracking-wider opacity-80">
                    voice {i + 1}
                  </span>
                  <span className={isTop ? "font-bold" : ""}>{nameOf(semis)}</span>
                </motion.div>
              );
            })}
          </div>

          <p className="mt-2 text-[11px] leading-snug text-cyan-300/80">
            Use this when the melody note is{" "}
            <strong className="text-amber-200">{inv.topRole}</strong>.
          </p>
        </motion.div>
      ))}
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard · live</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
